import React, { useState } from "react";
import styled from "styled-components";
import { X, ChevronDown, ChevronRight } from "lucide-react";
import { useLocation } from "react-router-dom";
import { healthRecordsData, templateData, aiRecommendations } from "../data/mockData";

const Container = styled.div`
  width: 100vw;
  min-height: 100vh;
  background-color: white;
  position: relative;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  color: #1d1d1f;
  padding: 20px 40px;
`;

const Header = styled.div`
  text-align: center;
  padding: 20px;
  background-color: white;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 30px;
  height: 30px;
  border: none;
  background: none;
  font-size: 24px;
  color: #8e8e93;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #1d1d1f;
  }
`;

const Title = styled.h1`
  font-size: 25px;
  font-weight: 400;
  color: #000000;
  margin-top: 10px;
`;

const PatientInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0;
  border-bottom: 1px solid #CFD3D4;
  margin: 20px 0;
`;

const PatientDetails = styled.div`
  font-size: 16px;
  color: #6F6F6F;
  font-weight: 400;
  line-height: 1.5;

  strong {
    color: #6F6F6F;
    font-weight: 700;
  }
`;

const HNNumber = styled.div`
  font-size: 16px;
  color: #6F6F6F;
  font-weight: 400;
`;

const RecordsList = styled.div`
  padding: 20px 0;
`;

const RecordItem = styled.div`
  background-color: white;
  border: 1px solid #CFD3D4;
  border-radius: ${props => props.isExpanded ? '8px 8px 0 0' : '8px'};
  ${props => props.isExpanded ? 'margin-bottom: 0;' : 'margin-bottom: 12px;'}
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f9f9fb;
  }

  &:active {
    background-color: #f0f0f2;
  }
`;

const RecordContent = styled.div`
  flex-grow: 1;
  display: flex;
  gap: 30px;
  align-items: center;
`;

const RecordDate = styled.div`
  font-size: 16px;
  color: #A3A3A3;
  font-weight: 400;
`;

const RecordTime = styled.div`
  font-size: 16px;
  color: #A3A3A3;
  font-weight: 400;
`;

const RecordDetails = styled.div`
  font-size: 16px;
  color: #A3A3A3;
  font-weight: 400;
`;

const Arrow = styled.div`
  color: #000000;
  font-size: 29px;
  width: 29px;
  height: 29px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 15px;
  transition: transform 0.2s ease;
  transform: ${props => props.isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'};
`;

const ExpandedContent = styled.div`
  padding: 1.5rem;
  background-color: white;
  border: 1px solid #CFD3D4;
  border-top: none;
  border-radius: 0 0 8px 8px;
  margin-bottom: 12px;
  display: ${props => props.isExpanded ? 'block' : 'none'};
`;

const PatientInfoGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 2fr 3fr;
  gap: 1.5rem;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #ECECEC;
`;

const InfoColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const InfoItem = styled.div`
  font-size: 14px;
  color: #6F6F6F;
`;

const InfoLabel = styled.span`
  font-weight: 700;
`;

const InfoValue = styled.span`
  font-weight: 400;
`;

const HNValue = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: #6F6F6F;
`;

const ParameterColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ParameterTable = styled.div`
  display: grid;
  grid-template-columns: 80px 80px 80px;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const ParameterTableHeader = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: #6F6F6F;
  text-align: center;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #ECECEC;
`;

const ParameterTableCell = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: #6F6F6F;
  text-align: center;
  padding: 0.25rem 0;
`;

const TemplateTag = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: transparent;
  border: 1px solid #00B5B8;
  border-radius: 0px;
  padding: 4px 8px;
  margin: 2px;
  font-size: 14px;
  color: #00B5B8;
  cursor: pointer;
  position: relative;
  width: fit-content;
`;

const RecommendationSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 1.5rem;
  margin-top: 1rem;
`;

const RecommendationColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const RecommendationTitle = styled.h3`
  font-size: 16px;
  font-weight: 400;
  color: #000000;
  margin: 0 0 1rem 0;
`;

const RecommendationImage = styled.div`
  width: 58px;
  height: 58px;
  background-color: #F3F4F6;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9CA3AF;
  font-size: 12px;
  margin-bottom: 0.5rem;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;

const RecommendationText = styled.div`
  font-size: 14px;
  color: #6F6F6F;
  line-height: 1.5;
  min-height: 120px;
`;

const HealthRecordsHistory = () => {
  const location = useLocation();
  const selectedRecord = location.state?.selectedRecord;
  const [expandedRecord, setExpandedRecord] = useState(null);

  const handleCloseModal = () => {
    window.history.back();
  };

  const handleRecordClick = (recordId) => {
    setExpandedRecord(expandedRecord === recordId ? null : recordId);
  };

  // สร้างประวัติการส่งผลสุขภาพจากข้อมูล selectedRecord
  const records = [
    {
      id: 1,
      date: selectedRecord?.date || "13/05/2025",
      time: "09:15 น.",
      doctor: "นพ. นพพงศ์ ถนอมศรี"
    },
    {
      id: 2,
      date: "23/01/2024",
      time: "15:22 น.",
      doctor: "นพ. นพพงศ์ ถนอมศรี"
    },
    {
      id: 3,
      date: "19/03/2023",
      time: "10:52 น.",
      doctor: "นพ. นพพงศ์ ถนอมศรี"
    }
  ];

  return (
    <Container>
      <Header>
        <CloseButton onClick={handleCloseModal}>
          <X size={20} />
        </CloseButton>
        <Title>ประวัติการส่งผลสุขภาพ</Title>
      </Header>

      <PatientInfo>
        <PatientDetails>
          คุณ <strong>{selectedRecord?.patientName?.replace(/^(นาย|นางสาว|นาง|ดร\.|ศ\.|รศ\.|ผศ\.)\s*/, '') || "สมชาย ใจดี"}</strong> เพศ : <strong>{selectedRecord?.gender || "ชาย"}</strong> อายุ : <strong>{selectedRecord?.age || "45"}</strong> โรคประจำตัว : <strong>{selectedRecord?.diseases || "ความดัน, ไขมันในเลือดสูง"}</strong>
        </PatientDetails>
        <HNNumber>HN: {selectedRecord?.hn || "VN-XXXXX-03"}</HNNumber>
      </PatientInfo>

      <RecordsList>
        {records.map((record) => (
          <div key={record.id}>
            <RecordItem
              isExpanded={expandedRecord === record.id}
              onClick={() => handleRecordClick(record.id)}
            >
              <RecordContent>
                <RecordDate>วันที่ {record.date}</RecordDate>
                <RecordTime>เวลา {record.time}</RecordTime>
                <RecordDetails>ส่งโดย {record.doctor}</RecordDetails>
              </RecordContent>
              <Arrow isExpanded={expandedRecord === record.id}>
                &gt;
              </Arrow>
            </RecordItem>

            <ExpandedContent isExpanded={expandedRecord === record.id}>
              {/* Patient Information Grid */}
              <PatientInfoGrid>
                <InfoColumn>
                  <InfoItem>
                    <InfoLabel>ชื่อ:</InfoLabel> <InfoValue>{selectedRecord?.patientName || "สมชาย ใจดี"}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>เพศ:</InfoLabel> <InfoValue>{selectedRecord?.gender || "ชาย"}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>อายุ:</InfoLabel> <InfoValue>{selectedRecord?.age || "45"}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>โรคประจำตัว:</InfoLabel> <InfoValue>{selectedRecord?.diseases || "ความดัน, ไขมันในเลือดสูง"}</InfoValue>
                  </InfoItem>
                </InfoColumn>

                <InfoColumn>
                  <InfoItem style={{ fontWeight: '400' }}>HN</InfoItem>
                  <HNValue>{selectedRecord?.hn || "VN-XXXXX-03"}</HNValue>
                </InfoColumn>

                <ParameterColumn>
                  <ParameterTable>
                    <ParameterTableHeader>พารามิเตอร์</ParameterTableHeader>
                    <ParameterTableHeader>ค่าที่ตรวจวัดได้</ParameterTableHeader>
                    <ParameterTableHeader>ค่าปกติ</ParameterTableHeader>

                    {(selectedRecord?.parameters || [
                      { name: 'CHO', result: 'xxx', normal: 'xxx' },
                      { name: 'TRI', result: 'xxx', normal: 'xxx' },
                      { name: 'HDL', result: 'xxx', normal: 'xxx' },
                      { name: 'LDL', result: 'xxx', normal: 'xxx' },
                      { name: 'CBC', result: 'xxx', normal: 'xxx' },
                      { name: 'FBS', result: 'xxx', normal: 'xxx' },
                      { name: 'SGPT', result: 'xxx', normal: 'xxx' },
                      { name: 'SGOT', result: 'xxx', normal: 'xxx' },
                      { name: 'BUN', result: 'xxx', normal: 'xxx' },
                      { name: 'Creatinine', result: 'xxx', normal: 'xxx' }
                    ]).map((param, index) => (
                      <React.Fragment key={index}>
                        <ParameterTableCell>{param.name}</ParameterTableCell>
                        <ParameterTableCell>{param.result}</ParameterTableCell>
                        <ParameterTableCell>{param.normal}</ParameterTableCell>
                      </React.Fragment>
                    ))}
                  </ParameterTable>
                </ParameterColumn>

                <InfoColumn>
                  <InfoItem style={{ fontWeight: '400', fontSize: '14px', color: '#6F6F6F', marginBottom: '0.5rem' }}>การแปลผลสุขภาพที่กำหนดไว้เบื้องต้น / Doctor Note</InfoItem>
                  <div>
                    {(selectedRecord?.templates || ['ตรวจสุขภาพทั่วไป', 'ตรวจไขมันในเลือด']).map((template, index) => (
                      <TemplateTag key={index}>
                        {template}
                      </TemplateTag>
                    ))}
                  </div>
                </InfoColumn>
              </PatientInfoGrid>

              {/* AI Recommendations Section */}
              <RecommendationSection>
                <RecommendationColumn>
                  <RecommendationTitle>คำแนะนำสุขภาพโดยหมอ</RecommendationTitle>
                  <RecommendationText>
                    {aiRecommendations.doctorAdvice}
                  </RecommendationText>
                </RecommendationColumn>

                <RecommendationColumn>
                  <RecommendationTitle>แนะนำการรับประทานอาหาร</RecommendationTitle>
                  <RecommendationImage style={{ backgroundImage: 'url(food.jpg)' }}>
                    {/* รูป */}
                  </RecommendationImage>
                  <RecommendationText>
                    {aiRecommendations.dietAdvice}
                  </RecommendationText>
                </RecommendationColumn>

                <RecommendationColumn>
                  <RecommendationTitle>แนะนำการออกกำลังกาย</RecommendationTitle>
                  <RecommendationImage style={{ backgroundImage: 'url(exercise.jpg)' }}>
                    {/* รูป */}
                  </RecommendationImage>
                  <RecommendationText>
                    {aiRecommendations.exerciseAdvice}
                  </RecommendationText>
                </RecommendationColumn>

                <RecommendationColumn>
                  <RecommendationTitle>แนะนำการนอน</RecommendationTitle>
                  <RecommendationImage style={{ backgroundImage: 'url(sleep.jpg)' }}>
                    {/* รูป */}
                  </RecommendationImage>
                  <RecommendationText>
                    {aiRecommendations.sleepAdvice}
                  </RecommendationText>
                </RecommendationColumn>
              </RecommendationSection>
            </ExpandedContent>
          </div>
        ))}
      </RecordsList>
    </Container>
  );
};

export default HealthRecordsHistory;